import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ServerSidePaginationAggrid from '../../../common/ServerSidePaginationAggrid.jsx';
import WorkQueuePriorityCell from '../../../work_queue/WorkQueuePriorityCell.jsx';
import WorkQueueAssigneesCell from '../../../work_queue/WorkQueueAssigneesCell.jsx';
import WorkQueueSubmissionCell from '../../../work_queue/WorkQueueSubmissionCell.jsx';
import styles from '../../../work_queue/WorkQueue.module.scss';
import TooltipWithRef from '../../../common/Tooltip.jsx';
import Button from '../../../bootstrap/Button.jsx';
import { fetchJson } from '../../../../helpers/fetch.js';
import useWorkQueueItemActions from '../../../work_queue/hooks/useWorkQueueItemActions.js';
import { gridThemeNoBorder } from '../../../common/AgGrid/gridTheme.js';

import {
  work_queue_my_items_path,
  work_queue_release_path
} from '~/routes';

const PAGE_SIZE = 20;
const SIDEBAR_WIDTH = { initial: 300, min: 240, max: 600 };
const CONDENSED_HEADER_HEIGHT = 40;
const CONDENSED_ROW_HEIGHT = 35;
const HEADER_HEIGHT = 48;
const ROW_HEIGHT = 64;
const PAGINATION_PANEL_HEIGHT = 42;

const VERTICAL_CENTER_STYLE = {
  display: 'flex',
  alignItems: 'center'
};

const formatDateTime = ({ value }) => (value ? DT.dt(value, { format: null, datetime: true, timezone: false }) : '');

function ActionsCell({ data, context, refresh, condensedMode }) {
  if (!data) return null;
  const { claim, release, saving } = useWorkQueueItemActions(data.work_queue_id, refresh);
  const isQueued = data.assignees.length === 0;
  const btnSize = condensedMode ? 'xs' : 'sm';

  return (
    <div className={styles.rowActions}>
      {!isQueued && <>
        <Button size={btnSize} type='default' disabled={saving} onClick={() => Turbo.visit(data.phase_path)}>
          {I18n.t('open')}
        </Button>
        <Button size={btnSize} type='default' disabled={saving} onClick={() => release(data.id)}>
          {I18n.t('work_queue_release')}
        </Button>
      </>}
      {isQueued && (
        <Button size={btnSize} disabled={saving} onClick={() => claim(data.id)}>
          {I18n.t('work_queue_claim')}
        </Button>
      )}
    </div>
  );
}

const DurationCellRenderer = ({ value }) => {
  if (!value) return null;

  return (
    <TooltipWithRef
      onClick={e => e.stopPropagation()}
      title={(value ? DT.dt(value, { format: null, datetime: true }) : '')}
      trigger='hover'
    >
      <span>{dayjs.duration(dayjs().diff(dayjs.utc(value).local())).humanize()}</span>
    </TooltipWithRef>
  );
}

const FILTER = {
  assigned: 'assigned',
  unassigned: 'unassigned'
};

const buildColumnDefs = (filter, refresh, condensedMode) => [
  {
    headerName: I18n.t('work_queue_priority'),
    field: 'priority',
    colId: 'priority',
    width: 100,
    cellStyle: VERTICAL_CENTER_STYLE,
    cellRenderer: WorkQueuePriorityCell,
    cellRendererParams: {
      itemQueueType: 'priority',
      condensedMode
    },
    hide: filter !== FILTER.unassigned,
    sortable: false,
  },
  {
    headerName: I18n.t('work_queue_submission'),
    colId: 'project_identifier',
    field: 'project_identifier',
    flex: 2,
    minWidth: 260,
    cellRendererParams: {
      condensedMode
    },
    cellRenderer: WorkQueueSubmissionCell,
    sortable: false
  },
  {
    headerName: I18n.t('work_queue_queued_at'),
    field: 'queued_at',
    colId: 'queued_at',
    width: 170,
    cellStyle: VERTICAL_CENTER_STYLE,
    cellRenderer: DurationCellRenderer,
    hide: filter !== FILTER.unassigned,
    sortable: false,
  },
  {
    headerName: I18n.t('time_assigned'),
    field: 'assigned_at',
    colId: 'assigned_at',
    width: 170,
    cellStyle: VERTICAL_CENTER_STYLE,
    cellRenderer: DurationCellRenderer,
    hide: filter !== FILTER.assigned,
    sortable: false,
  },
  {
    headerName: '',
    colId: 'actions',
    minWidth: 100,
    sortable: false,
    pinned: 'right',
    suppressHeaderMenuButton: true,
    cellRenderer: ActionsCell,
    cellRendererParams: {
      refresh,
      condensedMode
    }
  },
];

const ClaimNextButton = ({ item, refresh }) => {
  const { claim, saving } = useWorkQueueItemActions(item.work_queue_id, refresh);

  return (
    <Button size='sm' disabled={saving} onClick={() => claim(item)}>
      {I18n.t('claim_next')}
    </Button>
  );
};

function MyWorkWidget({ widgetId, editable, theme, widgetRef }) {
  const dashboardId = useSelector((state) => state.dashboard.dashboardId);
  const {
    myWorkTitle,
    upNextTitle,
    workQueueIds,
    listMode,
    waitingTooLongLimit,
    gridHeightMode = 'auto',
    visibleRows,
    condensedMode
  } = useSelector(state => state.dashboard.widgets[widgetId].settings);
  const [firstItem, setFirstItem] = useState(null);

  const headerHeight = condensedMode ? CONDENSED_HEADER_HEIGHT : HEADER_HEIGHT;
  const rowHeight = condensedMode ? CONDENSED_ROW_HEIGHT : ROW_HEIGHT;

  const assignedGridRef = useRef(null);
  const unassignedGridRef = useRef(null);

  const refresh = () => {
    assignedGridRef.current?.api?.refreshServerSide({ purge: true });
    unassignedGridRef.current?.api?.refreshServerSide({ purge: true });
  };

  const assignedColumnDefs = useMemo(() => buildColumnDefs(FILTER.assigned, refresh, condensedMode), [condensedMode]);
  const unassignedColumnDefs = useMemo(() => buildColumnDefs(FILTER.unassigned, refresh, condensedMode), [condensedMode]);

  const limitRef = useRef(waitingTooLongLimit);

  const gridHeight = useMemo(() => {
    if (gridHeightMode === 'auto') return null;
    if (!visibleRows) return null;
    return rowHeight * visibleRows + headerHeight + PAGINATION_PANEL_HEIGHT + 1;
  }, [visibleRows, gridHeightMode, rowHeight, headerHeight]);

  const gridProps = useMemo(() => ({
    fetchPath: work_queue_my_items_path({return_dashboard_id: dashboardId}),
    refreshBodyChanges: true,
    resizeToRowsHeight: gridHeight == null,
    style: {height: gridHeight},
    theme: gridThemeNoBorder.withParams({
      paginationPanelHeight: PAGINATION_PANEL_HEIGHT,
    }),
    defaultGridOptions: {
      domLayout: gridHeight === null ? 'autoHeight' : 'normal',
      rowHeight: rowHeight,
      headerHeight: headerHeight,
      pagination: true,
      paginationPageSize: PAGE_SIZE,
      paginationPageSizeSelector: [10, 20, 50],
      cacheBlockSize: PAGE_SIZE,
      overlayNoRowsTemplate: I18n.t('work_queue_no_items')
    }
  }), []);

  const getWorkingDays = (start, end) => {
    let days = 0;
    let current = start.startOf('day');

    while (current.isBefore(end, 'day')) {
      current = current.add(1, 'day');
      if (current.day() !== 0 && current.day() !== 6) {
        days++;
      }
    }

    return days;
  };

  const getRowClass = (params) => {
    const assignedAt = params.data?.assigned_at;
    if (!assignedAt || limitRef.current == null) return undefined;

    const days = getWorkingDays(dayjs.utc(assignedAt).local(), dayjs());
    if (days >= limitRef.current) {
      return 'waiting-too-long';
    }
  };

  useEffect(() => {
    limitRef.current = waitingTooLongLimit;
    assignedGridRef.current?.api?.redrawRows();
  }, [waitingTooLongLimit]);

  return (
    <div>
      {myWorkTitle && <h4>{myWorkTitle}</h4>}
      <ServerSidePaginationAggrid
        {...gridProps}
        fetchBody={{ filter: FILTER.assigned, work_queue_ids: listMode === 'chosen_queues' ? workQueueIds || [] : null }}
        externalGridRef={assignedGridRef}
        columnDefs={assignedColumnDefs}
        defaultGridOptions={{
          ...gridProps.defaultGridOptions,
          getRowClass: getRowClass
        }}
      />
      <hr style={{margin: '2rem 0'}} />

      {(firstItem || upNextTitle) &&
        <h4 style={{display: 'flex', justifyContent: 'space-between'}}>
          <span>{upNextTitle && upNextTitle}</span>
          {firstItem && (
            <ClaimNextButton
              key={firstItem.id}
              item={firstItem}
              refresh={refresh}
            />
          )}
        </h4>
        }
      
      <ServerSidePaginationAggrid
        {...gridProps}
        fetchBody={{ filter: FILTER.unassigned, work_queue_ids: listMode === 'chosen_queues' ? workQueueIds || [] : null }}
        externalGridRef={unassignedGridRef}
        columnDefs={unassignedColumnDefs}
        defaultGridOptions={{
          ...gridProps.defaultGridOptions,
          onPaginationChanged: (params) => {
            const firstRow = params.api.getDisplayedRowAtIndex(0);
            if (firstRow?.data) {
              setFirstItem(firstRow.data);
            }
          },
        }}
      />
    </div>
  )
}

export default MyWorkWidget;
